import { useState } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import LinearProgress from '@mui/material/LinearProgress'
import TablePagination from '@mui/material/TablePagination'

// What a data grid becomes on a phone.
//
// Measured on production at 390x600: the product grid came out 50/108/108/90,
// so Title -- the only column anyone actually reads -- got 108px, about thirteen
// characters, and the barcode sat past the right edge with no indication it was
// there. A grid answers "compare these rows across these fields", which is a
// question you ask on a monitor. On a phone the question is "which item is
// this", and the answer is a card.
//
// The selection contract is the grid's, deliberately: same `selectionModel` in,
// same array of ids out through `handleSelectChange`. A caller must not have to
// know which of the two it is talking to.

function nextSelection(current, id, allowMultiple) {
    if (current.includes(id)) return current.filter(entry => entry !== id)
    return allowMultiple ? [...current, id] : [id]
}

export default function CardList(props) {
    const {
        rows = [],
        renderCard,
        checkboxSelection = false,
        disableMultipleSelection = true,
        disableSelectionOnClick = false,
        // Opening a row and selecting it are different questions -- "show me this
        // one" versus "which of these do I want to act on" -- so a tap can only
        // mean one of them. Selection wins while it is live.
        onRowClick,
        selectionModel,
        handleSelectChange,
        loading = false,
        localeText = {},
        paginationMode = 'client',
        page,
        pageSize = 50,
        rowsPerPageOptions = [50],
        onPageChange,
        onPageSizeChange
    } = props

    // Users and Vendors hand over every row and never mention paging, so the
    // list keeps its own place when nobody else is holding it.
    const [innerPage, setInnerPage] = useState(0)
    const currentPage = page ?? innerPage
    const selected = selectionModel || []

    const server = paginationMode === 'server'
    const visible = server
        ? rows
        : rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    const count = server ? (props.rowCount ?? rows.length) : rows.length

    function handlePageChange(event, newPage) {
        if (onPageChange) return onPageChange(newPage)
        setInnerPage(newPage)
    }

    function toggle(id) {
        handleSelectChange?.(nextSelection(selected, id, !disableMultipleSelection))
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            {/* Kept above the list rather than replacing it: a refetch that blanks
                the screen loses the reader's place for the sake of saying
                "working". */}
            <Box sx={{ height: 4, flex: 'none' }}>{loading && <LinearProgress />}</Box>

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1, py: 1 }}>
                {visible.length === 0 && !loading && (
                    <Typography align='center' color='text.secondary' sx={{ py: 4 }}>
                        {localeText.noRowsLabel || 'No rows'}
                    </Typography>
                )}

                {visible.map(row => {
                    const isSelected = selected.includes(row.id)

                    return (
                        <Paper
                            key={row.id}
                            elevation={0}
                            // The grid selects a row on click unless the caller
                            // opts out, and Users and Vendors both depend on that
                            // -- neither asks for checkboxes, so without it there
                            // is no way to pick a row on a phone at all.
                            onClick={
                                disableSelectionOnClick
                                    ? (onRowClick ? () => onRowClick(row) : undefined)
                                    : () => toggle(row.id)
                            }
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                p: 1.5,
                                border: 1,
                                borderRadius: '12px',
                                // Selection reads as a fill and a border, not a
                                // tick alone -- the tick is 20px on a list you
                                // scan at arm's length.
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                bgcolor: isSelected ? 'action.selected' : 'background.paper',
                                cursor: !disableSelectionOnClick || onRowClick ? 'pointer' : 'default'
                            }}
                        >
                            {checkboxSelection && (
                                <Checkbox
                                    checked={isSelected}
                                    onChange={() => toggle(row.id)}
                                    // The card underneath also selects on click,
                                    // so without this one tap toggles twice and
                                    // lands back where it started.
                                    onClick={event => event.stopPropagation()}
                                    // The card's contents sit top-aligned, but a
                                    // tick that reads as "this row" belongs on the
                                    // row's middle, not level with its first line.
                                    sx={{ flex: 'none', alignSelf: 'center', width: 44, height: 44 }}
                                />
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>{renderCard(row)}</Box>
                        </Paper>
                    )
                })}
            </Box>

            {count > pageSize && (
                <TablePagination
                    component='div'
                    sx={{ flex: 'none' }}
                    count={count}
                    page={currentPage}
                    onPageChange={handlePageChange}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={rowsPerPageOptions}
                    onRowsPerPageChange={event => onPageSizeChange?.(Number(event.target.value))}
                    // Only the keys the locale actually defines: enUS ships an
                    // empty MuiTablePagination, and spreading an undefined
                    // getItemAriaLabel over MUI's own default removes it.
                    {...Object.fromEntries(
                        Object.entries(localeText.MuiTablePagination || {}).filter(([, value]) => value !== undefined)
                    )}
                />
            )}
        </Box>
    )
}
